import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, Play, Pause } from 'lucide-react';

export default function MediaCarousel({ media, onClose, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);

  if (!media || media.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="text-center text-white">
          <p>No media to display</p>
          <button
            onClick={onClose}
            className="mt-4 rounded-lg bg-white/20 px-4 py-2 hover:bg-white/30"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + 1) % media.length);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - 1 + media.length) % media.length);
  };

  const currentMedia = media[currentIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      <div className="relative h-full w-full max-w-6xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
        >
          <X className="h-6 w-6" />
        </button>

        {media.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        <div className="flex h-full items-center justify-center p-3 md:p-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="relative max-h-full max-w-full"
            >
              {currentMedia.type === 'video' ? (
                <div className="relative">
                  <video
                    src={currentMedia.url}
                    className="max-h-[70vh] max-w-full rounded-lg md:max-h-[80vh]"
                    controls
                    autoPlay={isPlaying}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                  <div className="absolute bottom-4 left-4">
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <img
                  src={currentMedia.url}
                  alt={currentMedia.caption || 'Media'}
                  className="max-h-[80vh] max-w-full rounded-lg object-contain"
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center text-white">
          <p className="text-lg font-medium">{currentMedia.caption || currentMedia.title}</p>
          <p className="text-sm opacity-75">
            {currentIndex + 1} of {media.length}
          </p>
        </div>

        {media.length > 1 && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2">
            <div className="flex space-x-2">
              {media.map((item, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-16 w-16 overflow-hidden rounded-lg border-2 transition-all ${
                    index === currentIndex ? 'border-white' : 'border-white/30'
                  }`}
                >
                  {item.type === 'video' ? (
                    <div className="flex h-full w-full items-center justify-center bg-gray-800">
                      <Play className="h-4 w-4 text-white" />
                    </div>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.caption || 'Thumbnail'}
                      className="h-full w-full object-cover"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
